import { useState, useEffect, useRef } from 'react'
import OtpInput from 'react-otp-input'
import api, { BASE_URL } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import axios from 'axios'
import { IoIosArrowRoundBack } from 'react-icons/io'
import useAuth from '../../context/userContext'
import Cookies from "js-cookie"
import { useHistory } from "react-router-dom"
import { rejectInactiveAccount } from '../../utils/accessControl'

const TwoFALogin = () => {
  const router = useHistory()
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [waitTime, setWaitTime] = useState(30) // initial wait time in seconds
  const [currentWait, setCurrentWait] = useState(0)
  const [resendCount, setResendCount] = useState(0)
  const timerRef = useRef(null)
  const { setUser, setUserType, setFirstTime, setisAuthenticated, setRedirectToReferrer, syncCommoditySelectionModal } = useAuth()

  useEffect(() => {
    if (currentWait > 0) {
      timerRef.current = setInterval(() => {
        setCurrentWait((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(timerRef.current)
  }, [currentWait])

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const { data } = await axios.post(`${BASE_URL}auth/verify-two-factor/`, {
        otp_code: otp,
      });

      if (data) {
        if (rejectInactiveAccount(data.user, ErrorNotification)) {
          return
        }

        SuccessNotification(data?.message || "Two-factor authentication successful")
        Cookies.set("token", data.tokens?.access, { expires: 60 })
        api.defaults.headers.Authorization = `Bearer ${data.tokens?.access}`
        const userData = {
          ...data.user,
          ...(typeof data.commodity_selection_required === 'boolean'
            ? { commodity_selection_required: data.commodity_selection_required }
            : {}),
        }
        window.localStorage.setItem("user", JSON.stringify(userData))
        window.localStorage.setItem("token", data.tokens?.access)
        window.localStorage.setItem("refresh_token", data.tokens?.refresh)
        window.localStorage.setItem(
          "is_firsttime",
          data?.user?.is_firsttime ? "true" : "false"
        )
        window.localStorage.setItem(
          "verification_required",
          data?.verification_required ? "true" : "false"
        )
        setUser(userData)
        syncCommoditySelectionModal?.(data)
        setFirstTime(Boolean(data.user.is_firsttime))
        setUserType(data.user.user_type)
        setRedirectToReferrer(true)
        setisAuthenticated(true)
        router.push("/admin/dashboard");
      } else {
        ErrorNotification(data.error)
      }
    } catch (error) {
      ErrorNotification(error.response.data.error)
    } finally {
      setLoading(false)
    }
  }

  const resendCode = async () => {
    setPasswordLoading(true)
    const rawEmail = localStorage.getItem("email")
    const email =
      typeof rawEmail === "string" ? rawEmail.replace(/^"|"$/g, "").trim() : ""

    try {
      const { data } = await axios.post(`${BASE_URL}auth/resend-otp/`, { email })
      SuccessNotification(
        data?.message ||
          `We've sent a new 6-digit code to ${email}. Use the latest code.`
      )
      setResendCount((prev) => prev + 1)
      setWaitTime((prev) => prev + 30)
      setCurrentWait(waitTime + 30)
    } catch (error) {
      ErrorNotification(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message
      )
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <div className="w-full h-screen flex items-center justify-center  background dark:bg-dark">
      <div className="p-5 rounded-md max-w-[400px] mx-auto  w-full">
        <div className="flex content-center justify-center mb-4 ">
          <div className="w-16 h-16 bg-yellowcolor/10 rounded-full flex items-center justify-center">
            <div className="w-12 h-12 bg-yellowcolor/30 dark:bg-[#FEECB8] rounded-full flex items-center justify-center">
              eee
            </div>
          </div>
        </div>
        <h1 className="text-center font-semibold dark:text-white text-3xl mb-3 capitalize from-neutral-900">
          Enter 2FA code
        </h1>
        <div className="">
          <p className="text-center text-slate-400 font-normal text-base">
            Please enter the verification code sent to your email address ({localStorage.getItem("email")}) or phone number.
          </p>
          <div className='w-full'>
            <div className="my-7 w-full">
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                renderSeparator={<span></span>}
                renderInput={(props) => <input
                  {...props}
                  className='w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400'
                />}
                inputStyle={{
                  width: '100%',
                  height: '65px',
                  fontSize: '16px',
                  fontWeight: 'normal',
                  border: '2px solid #E5B117',
                  borderRadius: '8px',
                  padding: '10px 0',
                }}
                containerStyle={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5
                }}
              />
            </div>

            <div className="my-4 space-y-4">
              <button
                disabled={loading || otp.length !== 6}
                onClick={handleSubmit}
                type="submit"
                className="bg-yellowcolor w-full capitalize hover:bg-yellowcolor/90  text-black font-normal py-3 px-10 rounded-md"
              >
                <p>{loading ? "Please wait..." : "Verify"} </p>
              </button>
              <div className="flex items-center justify-center">
                <button
                  onClick={resendCode}
                  disabled={passwordLoading || currentWait > 0}
                  className="w-full outline-none border-none flex flex-row items-center justify-center text-center font-normal text-black dark:text-white mt-2">
                  Didn't receive code?&nbsp;
                  <p className={`text-yellowcolor hover:underline font-medium underline ${passwordLoading ? "text-gray-500" : ""}`}>
                    {passwordLoading
                      ? "Please wait..."
                      : currentWait > 0
                        ? `Resend in ${currentWait}s`
                        : "Resend code"}
                  </p>
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center w-full justify-center">
            <a href="/">
              <button
                className="outline-none border-none font-normal flex flex-row items-center gap-x-2 text-sm text-black dark:text-white"
              >
                <IoIosArrowRoundBack className="text-2xl" />
                <p>Back to log in</p>
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>

  )
}

export default TwoFALogin